<template>
  <div v-if="visible" data-testid="email-verification-banner">
    <AlertBanner
      v-if="feedbackMessage"
      :status="feedbackStatus"
      class="banner-below-nav !leading-10"
      :full-page-banner="true"
      :auto-scroll-to-banner="false"
      data-testid="email-verification-banner-feedback"
    >
      <span class="inline-block px-16 py-8">{{ feedbackMessage }}</span>
    </AlertBanner>
    <AlertBanner
      :status="helperStatus.Warn"
      class="banner-below-nav !leading-10"
      :class="{ 'banner-below-feedback': !!feedbackMessage }"
      :full-page-banner="true"
      :auto-scroll-to-banner="false"
    >
      <span class="inline-block px-16 py-8">
        Verify your email with the link sent to
        <span class="font-medium" data-testid="email-verification-banner-address">{{ displayedEmail }}</span
        >.
        <button
          type="button"
          class="underline font-medium cursor-pointer"
          data-testid="email-verification-banner-resend"
          :disabled="resending"
          @click="resendVerificationEmail"
        >
          Resend link
        </button>
      </span>
    </AlertBanner>
    <!-- spacers: each fixed banner is ~3.5rem (leading-10 + py-8) -->
    <div class="h-56 w-full" aria-hidden="true">&nbsp;</div>
    <div v-if="feedbackMessage" class="h-56 w-full" aria-hidden="true">&nbsp;</div>
  </div>
</template>

<script lang="ts">
import AlertBanner from '@/components/alert_banner/AlertBanner.vue';
import helperStatus from '@/helpers/status';
import shouldShowEmailVerificationBanner from '@/helpers/emailVerificationBannerLogic';
import { shouldForceParentExperimentEmailVerificationBanner } from '@/helpers/shouldForceParentExperimentEmailVerificationBanner';
import { EMAIL_VERIFICATION_STATUS } from '@/graphql/account/queries';
import { SEND_VERIFICATION_EMAIL } from '@/graphql/account/mutations';
import { EMAIL_VERIFICATION_POLL_INTERVAL_MS } from '@/apps/verifyEmail/useVerifyEmailWaiting';
import { invalidateEmailVerificationStatus } from '@/apps/verifyEmail/helpers/emailVerificationStatus';

type GraphQLErrorLike = {
  message: string;
  extensions?: { error_code?: string };
};

const FEEDBACK_DISMISS_MS = 5000;

export default {
  name: 'EmailVerificationBanner',
  components: {
    AlertBanner,
  },
  emits: ['visibility-change'],
  data() {
    return {
      helperStatus,
      showBannerFromApi: false,
      forceShowFromExperiment: false,
      email: '',
      loaded: false,
      resending: false,
      feedbackMessage: '',
      feedbackStatus: helperStatus.Valid as number,
      feedbackTimer: null as ReturnType<typeof setTimeout> | null,
      pollTimer: null as ReturnType<typeof setTimeout> | null,
      wasVisible: false,
    };
  },
  computed: {
    visible(): boolean {
      if (!this.loaded) {
        return false;
      }
      return shouldShowEmailVerificationBanner(this.showBannerFromApi, this.$route.path, {
        forceShow: this.forceShowFromExperiment,
      });
    },
    displayedEmail(): string {
      return this.email || 'your email address';
    },
  },
  watch: {
    '$route.fullPath'() {
      void this.refreshStatus();
    },
    visible: {
      immediate: true,
      handler(isVisible: boolean) {
        this.$emit('visibility-change', isVisible);
        if (isVisible) {
          this.wasVisible = true;
          this.scheduleNextPoll();
          return;
        }
        if (this.wasVisible) {
          invalidateEmailVerificationStatus();
          this.wasVisible = false;
        }
        this.clearFeedback();
        this.stopPolling();
      },
    },
  },
  mounted() {
    void this.refreshStatus();
    window.addEventListener('focus', this.onWindowFocus);
  },
  beforeUnmount() {
    window.removeEventListener('focus', this.onWindowFocus);
    this.clearFeedback();
    this.stopPolling();
    this.$emit('visibility-change', false);
  },
  methods: {
    onWindowFocus(): void {
      void this.refreshStatus();
    },
    scheduleNextPoll(): void {
      if (!this.visible) {
        return;
      }
      this.stopPolling();
      this.pollTimer = setTimeout(() => {
        void this.pollOnce();
      }, EMAIL_VERIFICATION_POLL_INTERVAL_MS);
    },
    async pollOnce(): Promise<void> {
      if (!this.visible) {
        return;
      }
      await this.refreshStatus();
      if (this.visible) {
        this.scheduleNextPoll();
      }
    },
    stopPolling(): void {
      if (this.pollTimer !== null) {
        clearTimeout(this.pollTimer);
        this.pollTimer = null;
      }
    },
    async refreshStatus(): Promise<void> {
      try {
        const { data } = await this.$apollo.query({
          query: EMAIL_VERIFICATION_STATUS,
          fetchPolicy: 'network-only',
        });
        const status = data?.emailVerificationStatus;
        const verified = !!status?.verified;
        this.showBannerFromApi = !!status?.showBanner;
        this.forceShowFromExperiment = shouldForceParentExperimentEmailVerificationBanner(
          verified,
          !!status?.emailVerificationRequired
        );
        if (status?.email) {
          this.email = status.email;
        }
      } catch (error) {
        console.error('Error checking email verification banner eligibility:', error);
        this.showBannerFromApi = false;
        this.forceShowFromExperiment = false;
      } finally {
        this.loaded = true;
      }
    },
    async resendVerificationEmail(): Promise<void> {
      if (this.resending) {
        return;
      }

      this.resending = true;
      try {
        await this.$apollo.mutate({
          mutation: SEND_VERIFICATION_EMAIL,
        });
        this.showFeedback(
          "Verification email re-sent. Check your email to verify and don't forget to check your spam folder.",
          helperStatus.Valid
        );
      } catch (error: unknown) {
        const graphqlError = (error as { graphQLErrors?: GraphQLErrorLike[] })?.graphQLErrors?.[0];
        if (graphqlError?.extensions?.error_code === 'rate_limit_exceeded') {
          this.showFeedback(graphqlError.message, helperStatus.Error);
        } else {
          this.showFeedback('Something went wrong sending your verification email. Please try again.', helperStatus.Error);
          console.error(error);
        }
      } finally {
        this.resending = false;
      }
    },
    showFeedback(message: string, status: number): void {
      this.clearFeedback();
      this.feedbackMessage = message;
      this.feedbackStatus = status;
      this.feedbackTimer = setTimeout(() => {
        this.clearFeedback();
      }, FEEDBACK_DISMISS_MS);
    },
    clearFeedback(): void {
      if (this.feedbackTimer !== null) {
        clearTimeout(this.feedbackTimer);
        this.feedbackTimer = null;
      }
      this.feedbackMessage = '';
    },
  },
};
</script>

<style scoped lang="scss">
/* Stack the EV prompt beneath the temporary resend feedback banner (Rails: flash then EV). */
:deep(.banner-below-feedback) {
  top: calc(48px + 3.5rem);

  @media (min-width: 768px) {
    top: calc(80px + 3.5rem);
  }
}
</style>
